import type Draw from "ol/interaction/Draw";
import type LineString from "ol/geom/LineString";
import type Polygon from "ol/geom/Polygon";
import type { DrawToolKind } from "src/domains/shared/utils/annotationDrawSession";

/**
 * 더블클릭으로 끝나는 선·면 Draw 의 `minPoints`. OL 은 확정 점이 `minPoints` 에 못 미치면(미만) 첫·마지막 점 위의
 * 클릭을 완료 시도(`finishCondition`)로 보지 않고 점을 하나 더 찍는다 — 더블클릭의 두 번째 클릭이 같은 자리에
 * 중복점을 남기고, 그 중복점이 확정 점으로 세어져 꼭짓점 두 개짜리 면이 완료되었다(ol 10.6 실측).
 * 재클릭 판정은 항상 서게 두고(1), 완료 여부는 호출부의 `finishCondition` 과 더블클릭 처리가 확정 점 수로 정한다.
 * `trace` 를 켠 Draw 에서는 그래도 두 번째 클릭이 점을 더 찍을 수 있다 — 첫 클릭이 기존 도형 위에서 trace 를 켜면
 * 두 번째 클릭은 trace 판정(선은 재클릭 판정 없음, 면은 첫 점만 비교)을 받는다. 그 보정은
 * `useDrawSketchGestures` 의 dblclick 처리가 한다.
 */
export const FINISH_BY_CONDITION_MIN_POINTS = 1;

// OL Draw 스케치 실측(ol 10.6): LineString 좌표의 마지막 1개는 커서 추적 좌표,
// Polygon 외곽 링의 마지막 2개는 커서 추적 좌표 + 닫힘 좌표라 확정 점이 아니다.
const LINE_UNCONFIRMED_TAIL = 1;
const POLYGON_UNCONFIRMED_TAIL = 2;
// 단 ol 10.6 `startDrawing_` 직후(첫 클릭 뒤 포인터 갱신 전) 면 링은 닫힘 좌표 없이 [꼭짓점, 커서] 라 커서만 뺀다.
const UNCLOSED_POLYGON_UNCONFIRMED_TAIL = 1;
const CLOSED_POLYGON_RING_MIN_LENGTH = 1 + POLYGON_UNCONFIRMED_TAIL; // 꼭짓점 1 + 커서 + 닫힘

function findSketchGeometry<T>(draw: Draw, type: "LineString" | "Polygon") {
  const features = draw.getOverlay().getSource()?.getFeatures() ?? [];
  for (const feature of features) {
    const geometry = feature.getGeometry();
    if (geometry?.getType() === type) return geometry as T;
  }
  return null;
}

function countWithoutTail(total: number, unconfirmedTail: number): number {
  return Math.max(total - unconfirmedTail, 0);
}

/**
 * 그리는 중인 선·면·사각형 스케치의 확정 점 개수를 돌려준다(커서 추적·닫힘 좌표 제외, 스케치가 없거나 그 밖의 도구면 0).
 * 사용자가 클릭한 점뿐 아니라 trace 중 OL 이 기존 도형의 변을 따라 끼워 넣은 꼭짓점(클릭 전 커서를 따라가며 끼운 것 포함)도
 * 센다. 면은 첫 클릭 직후 닫힘 좌표가 붙기 전에도 그 꼭짓점을 센다.
 */
export function confirmedSketchPointCount(
  draw: Draw,
  kind: DrawToolKind
): number {
  if (kind === "line") {
    const line = findSketchGeometry<LineString>(draw, "LineString");
    const coordinates = line?.getCoordinates() ?? [];
    return countWithoutTail(coordinates.length, LINE_UNCONFIRMED_TAIL);
  }
  if (kind === "polygon") {
    const polygon = findSketchGeometry<Polygon>(draw, "Polygon");
    const ring = polygon?.getCoordinates()[0] ?? [];
    const unconfirmedTail =
      ring.length < CLOSED_POLYGON_RING_MIN_LENGTH
        ? UNCLOSED_POLYGON_UNCONFIRMED_TAIL
        : POLYGON_UNCONFIRMED_TAIL;
    return countWithoutTail(ring.length, unconfirmedTail);
  }
  if (kind === "rectangle") {
    // createBox 스케치는 시작점 좌표를 보존하지 않는다 — 스케치 존재 여부만 확정 점 1개로 읽는다
    return findSketchGeometry<Polygon>(draw, "Polygon") ? 1 : 0;
  }
  return 0;
}
